import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useResponsive } from '../../../context/ResponsiveContext';
import Breadcrumbs from '../../Breadcrumbs/Breadcrumbs';
import Button from '../../Button/Button';
import FaqPopup from '../../FaqPopup/FaqPopup';
import { ArrowLeftIcon, QuestionBubbleIcon, QuestionIcon } from '../../Icon/Icon';
import Loader from '../../Loader/Loader';
import MobileWidget from '../../MobileWidget/MobileWidget';
import Tooltip from '../../Tooltip/Tooltip';
import {
  FaqButtonStyled,
  FeedbackWrapperStyled,
  InnerContainerStyled,
  LeftSideStyled,
  NavSectionStyled,
  PageWrapperStyled,
  RightNavControlsStyled,
  RightSideStyled,
  RightSideTitleStyled,
  TopRightSideStyled,
  WrapperStyled,
} from './PageLayout.styled';

type PageLayoutPropsT = {
  leftSideContent: React.ReactNode;
  rightSideContent?: React.ReactNode;
  topSectionContent?: React.ReactNode;
  topSectionContentRight?: React.ReactNode;
  isLoading?: boolean;
  isNextPageAvailable?: boolean;
  backButtonLabel?: React.ReactNode;
  onClickBack?: () => void;
  onClickBreadcrumb?: () => void;
};

const PageLayout: React.FC<PageLayoutPropsT> = ({
  topSectionContent,
  topSectionContentRight,
  leftSideContent,
  rightSideContent,
  isLoading,
  isNextPageAvailable,
  backButtonLabel,
  onClickBack,
  onClickBreadcrumb,
}) => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const [isFaqPopupOpen, setIsFaqPopupOpen] = useState(false);

  if (isLoading) {
    return <Loader />;
  }

  const handleOnClickBack = () => {
    if (onClickBack) {
      onClickBack();

      return;
    }

    navigate(-1);
  };

  return (
    <>
      <NavSectionStyled>
        <InnerContainerStyled>
          <Breadcrumbs
            isNextPageAvailable={isNextPageAvailable}
            onClickBreadcrumb={onClickBreadcrumb}
          />

          {isMobile ? (
            <MobileWidget onClickFaqButton={() => setIsFaqPopupOpen(true)} />
          ) : (
            <RightNavControlsStyled>
              <FeedbackWrapperStyled>
                <a href="mailto:contact@wayaj.com">
                  <FormattedMessage id="common.giveFeedback" defaultMessage="Give feedback" />
                </a>
                <Tooltip element={<QuestionIcon />}>
                  <FormattedMessage
                    id="giveFeedBack.tooltip.text"
                    defaultMessage="Did you encounter any problems or anything was not clear? Help us improve. We are a young company and your opinion counts!"
                  />
                </Tooltip>
              </FeedbackWrapperStyled>

              <FaqButtonStyled onClick={() => setIsFaqPopupOpen(true)}>
                <FormattedMessage id="button.FAQ" defaultMessage="FAQ" />
                <QuestionBubbleIcon />
              </FaqButtonStyled>
            </RightNavControlsStyled>
          )}
        </InnerContainerStyled>
      </NavSectionStyled>

      <PageWrapperStyled>
        <Button
          modifier="inline"
          onClick={handleOnClickBack}
          isUpperCase={false}
          icon={<ArrowLeftIcon />}
        >
          {backButtonLabel || <FormattedMessage id="common.back" defaultMessage="Back" />}
        </Button>

        {!!topSectionContent && (
          <WrapperStyled>
            <LeftSideStyled>{topSectionContent}</LeftSideStyled>
            {(!!rightSideContent || !!topSectionContentRight) && (
              <TopRightSideStyled>{topSectionContentRight}</TopRightSideStyled>
            )}
          </WrapperStyled>
        )}

        <WrapperStyled>
          <LeftSideStyled>{leftSideContent}</LeftSideStyled>
          {!!rightSideContent && (
            <RightSideStyled>
              <RightSideTitleStyled level={3}>
                <FormattedMessage id="common.yourCart" defaultMessage="Your selection" />
              </RightSideTitleStyled>{' '}
              {rightSideContent}
            </RightSideStyled>
          )}
        </WrapperStyled>
      </PageWrapperStyled>

      <FaqPopup isOpen={isFaqPopupOpen} onClose={() => setIsFaqPopupOpen(false)} />
    </>
  );
};

export default PageLayout;
